import { useState, useEffect, useCallback } from 'react';
import { Link, useLocation } from 'react-router-dom';

const navLinks = [
  { label: '首页', href: '/' },
  { label: '电影', href: '/category/movie' },
  { label: '电视剧', href: '/category/tv' },
  { label: '综艺', href: '/category/variety' },
  { label: '动漫', href: '/category/anime' },
  { label: '纪录片', href: '/category/documentary' },
  { label: '音乐MV', href: '/category/music' },
  { label: '资讯', href: '/article/ai-video-trends' },
  { label: '关于我们', href: '/about' },
];

export default function Navbar() {
  const [scrolled, setScrolled] = useState(false);
  const [mobileOpen, setMobileOpen] = useState(false);
  const [searchOpen, setSearchOpen] = useState(false);
  const location = useLocation();

  useEffect(() => {
    const handleScroll = () => {
      setScrolled(window.scrollY > 40);
    };
    window.addEventListener('scroll', handleScroll, { passive: true });
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  useEffect(() => {
    setMobileOpen(false);
  }, [location.pathname]);

  const handleSearchToggle = useCallback(() => {
    setSearchOpen((prev) => !prev);
  }, []);

  const isActive = (href: string) => {
    if (href === '/') return location.pathname === '/';
    return location.pathname.startsWith(href);
  };

  return (
    <header
      className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${
        scrolled
          ? 'bg-foreground-950/95 backdrop-blur-md shadow-lg'
          : 'bg-foreground-950'
      }`}
    >
      <div className="w-full">
        <nav className="flex items-center justify-between px-4 md:px-6 lg:px-8 py-3 md:py-4">
          <Link
            to="/"
            className="flex items-center gap-2 text-white font-bold text-lg md:text-xl tracking-tight shrink-0"
            aria-label="91视频网站首页"
          >
            <span className="w-8 h-8 md:w-9 md:h-9 bg-primary-500 rounded-lg flex items-center justify-center text-white text-sm font-black">
              91
            </span>
            <span className="hidden sm:inline">视频网站</span>
          </Link>

          <div className="hidden lg:flex items-center gap-1">
            {navLinks.map((link) => (
              <Link
                key={link.href}
                to={link.href}
                className={`px-3 py-2 text-sm whitespace-nowrap rounded-md transition-colors ${
                  isActive(link.href)
                    ? 'text-primary-500 bg-primary-500/10'
                    : 'text-foreground-300 hover:text-white hover:bg-white/5'
                }`}
              >
                {link.label}
              </Link>
            ))}
          </div>

          <div className="flex items-center gap-2 md:gap-3">
            <button
              onClick={handleSearchToggle}
              className="w-9 h-9 flex items-center justify-center text-foreground-300 hover:text-white transition-colors"
              aria-label="搜索"
            >
              <i className="ri-search-line text-lg" />
            </button>

            <button
              onClick={() => setMobileOpen(!mobileOpen)}
              className="lg:hidden w-9 h-9 flex items-center justify-center text-foreground-300 hover:text-white transition-colors"
              aria-label="菜单"
              aria-expanded={mobileOpen}
            >
              <i className={`ri-${mobileOpen ? 'close' : 'menu'}-line text-xl`} />
            </button>

            <Link
              to="/about"
              className="hidden md:inline-flex items-center gap-1.5 px-4 py-2 bg-primary-500 text-white text-sm font-medium rounded-full hover:bg-primary-600 transition-colors whitespace-nowrap"
            >
              <i className="ri-user-line" />
              登录
            </Link>
          </div>
        </nav>

        {searchOpen && (
          <div className="px-4 md:px-6 lg:px-8 pb-4 animate-fadeIn">
            <div className="flex items-center gap-2 bg-foreground-900 rounded-xl px-4 py-3 border border-foreground-800">
              <i className="ri-search-line text-foreground-500" />
              <input
                type="text"
                placeholder="搜索视频、电影、剧集..."
                className="flex-1 bg-transparent text-white text-sm outline-none placeholder:text-foreground-600"
                autoFocus
              />
              <button
                onClick={() => setSearchOpen(false)}
                className="text-foreground-500 hover:text-foreground-300 text-xs whitespace-nowrap"
              >
                关闭
              </button>
            </div>
          </div>
        )}

        {mobileOpen && (
          <div className="lg:hidden bg-foreground-950 border-t border-foreground-800 px-4 py-4 space-y-1 animate-fadeIn">
            {navLinks.map((link) => (
              <Link
                key={link.href}
                to={link.href}
                className={`block px-4 py-3 text-sm rounded-lg transition-colors ${
                  isActive(link.href)
                    ? 'text-primary-500 bg-primary-500/10'
                    : 'text-foreground-300 hover:text-white hover:bg-white/5'
                }`}
              >
                {link.label}
              </Link>
            ))}
          </div>
        )}
      </div>
    </header>
  );
}